import React from 'react';
import { Box, Typography } from '@mui/material';
import type { PasswordValidation } from '../helpers/passwordUtils';
import { PASSWORD_POLICY_MIN_LENGTH } from '../helpers/passwordUtils';

interface PasswordPolicyIndicatorProps {
  /** Validation result from validatePasswordRequirements(password) */
  validation: PasswordValidation;
  /** When true, the requirements list is shown (e.g. when user has typed in password field) */
  show: boolean;
}

/**
 * Displays the shared password policy requirements with success/error styling.
 * Use with validatePasswordRequirements() from passwordUtils in Register,
 * CompanySignup, ResetPassword, etc.
 */
const PasswordPolicyIndicator: React.FC<PasswordPolicyIndicatorProps> = ({
  validation,
  show,
}) => {
  if (!show) return null;

  return (
    <Box sx={{ mt: 0, mb: 1 }}>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
        Password Requirements:
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography
          variant="body2"
          sx={{
            color: validation.minLength ? 'success.main' : 'error.main',
            fontSize: '0.875rem',
          }}
        >
          • At least {PASSWORD_POLICY_MIN_LENGTH} characters
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: validation.hasLowercase ? 'success.main' : 'error.main',
            fontSize: '0.875rem',
          }}
        >
          • At least 1 lowercase letter (a-z)
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: validation.hasUppercase ? 'success.main' : 'error.main',
            fontSize: '0.875rem',
          }}
        >
          • At least 1 uppercase letter (A-Z)
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: validation.hasNumber ? 'success.main' : 'error.main',
            fontSize: '0.875rem',
          }}
        >
          • At least 1 number (0-9)
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: validation.hasSpecialChar ? 'success.main' : 'error.main',
            fontSize: '0.875rem',
          }}
        >
          • At least 1 special character (eg. !@#$%^*)
        </Typography>
      </Box>
    </Box>
  );
};

export default PasswordPolicyIndicator;
